import { Modal } from '@lmc/ui-kit'
import Text from '@lmc/ui-kit/src/general/Typography/Text/Text'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Warning } from '@lmc/ui-kit/src/systemIcons'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const MAX_REASON_LENGTH = 200

interface ArchiveDocumentModalProps {
  isVisible: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}

const ArchiveDocumentModal: React.FC<ArchiveDocumentModalProps> = ({
  isVisible,
  onClose,
  onConfirm
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const [reason, setReason] = useState('')

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason)
      setReason('')
    }
  }

  const handleCancel = () => {
    onClose()
    setReason('')
  }

  const isConfirmDisabled = !reason.trim()

  return (
    <Modal
      isModalVisible={isVisible}
      setIsModalVisible={onClose}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      confirmDisabled={isConfirmDisabled}
      closeButton={true}
      localisation={{
        confirmText: t(reportsConstants.ARCHIVE_DOCUMENT_CONFIRM),
        cancelText: t(reportsConstants.ARCHIVE_DOCUMENT_CANCEL)
      }}
    >
      <ModalContent>
        <TitleContainer>
          <Warning size={20} color="#DE7C12" />
          <StyledTitle>
            {t(reportsConstants.ARCHIVE_DOCUMENT_TITLE)}
          </StyledTitle>
        </TitleContainer>
        <StyledDivider />
        <ContentSection>
          <Text level={1}>
            {t(reportsConstants.ARCHIVE_DOCUMENT_REASON_DESCRIPTION)}
          </Text>
        </ContentSection>
        <ContentSection>
          <Text level={1} weight="medium">
            {t(reportsConstants.ARCHIVE_DOCUMENT_NOTE)}
          </Text>
        </ContentSection>
        <FormSection>
          <Text level={1} weight="bold">
            {t(reportsConstants.ARCHIVE_DOCUMENT_REASON_LABEL)}{' '}
            <RedAsterisk>*</RedAsterisk>
          </Text>
          <TextAreaContainer>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t(reportsConstants.ARCHIVE_DOCUMENT_PLACEHOLDER)}
              rows={4}
              maxLength={MAX_REASON_LENGTH}
              data-testid="archive-document-reason"
            />
            <CharacterCount data-testid="archive-document-char-count">
              {reason.length}/{MAX_REASON_LENGTH}
            </CharacterCount>
          </TextAreaContainer>
        </FormSection>
        <StyledDivider />
      </ModalContent>
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`

const ContentSection = styled.div`
  margin-bottom: 24px;
`

const StyledTitle = styled(Text)`
  font-size: 24px;
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledDivider = styled(Divider)`
	margin: 24px 0;
`

const RedAsterisk = styled.span`
  color: ${(props) => props.theme.color.category.incident};
`

const TextAreaContainer = styled.div`
  position: relative;
`

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 100px;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  &::placeholder {
    color: #bfbfbf;
  }
`

const CharacterCount = styled.span`
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: #bfbfbf;
`

export default ArchiveDocumentModal
